import type * as PanelTypes from '../../state/views/panel/types';
import type {Ref as PanelBankSectionRef} from '../../state/views/panelBankSectionConfig/types';
import type {RunHistoryKeyInfo, RunHistoryKeyType} from '../../types/run';
import {type ColorPaletteOption} from '../../util/colors';
import type {
  LayedOutPanel,
  LayedOutPanelWithRef,
  LayoutParameters,
  PanelConfig,
} from '../../util/panelTypes';
import {type MediaBrowserPanelConfig} from '../PanelMediaBrowser/common';
import {type VegaPanelConfig} from '../PanelVega/common';
import {type SemanticLegendPalette} from '../WorkspaceDrawer/Settings/semanticLegend/utils/colorPalettes';
import type {
  LinePlotSettings,
  OrganizationPrefix,
  PointVisualizationOptions,
  RunColorOptions,
  Settings,
  TooltipNumberOfRunsOptions,
  WorkspaceLayoutSettings,
} from '../WorkspaceDrawer/Settings/types';

export enum PanelBankConfigState {
  Init,
  Ready,
}

export enum SectionPanelSorting {
  None,
  Manual,
  Alphabetical,
}

export interface DefaultNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  keyType: RunHistoryKeyType;
  defaultSection: string;
  defaultXAxis?: string;
}

export interface RegexPanelSpec {
  type: 'regex-panel';
  keyType: 'number';
  metricRegex: string;
  defaultSection: string;
  /**
   * If true, other panels with keys matching metricRegex should be removed from the panel bank.
   * A "merged" panel combines/consolidates multiple panels into a single panel using regex.
   * This helps reduce noise & redundant panels in the workspace.
   */
  isMergedPanel: boolean;
}

export interface DefaultVegaPanelSpec {
  type: 'legacy-vega';
  keyType: RunHistoryKeyType;
  config: VegaPanelConfig;
  defaultSection: string;
}

interface FoundNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  panelId: string;
  defaultXAxis?: string | null;
}

interface FoundVegaPanelSpec {
  type: 'legacy-vega';
  viz: VegaPanelConfig;
  panelId: string;
}

export interface DefaultPanelSpecs {
  [key: string]: DefaultNonVegaPanelSpec | DefaultVegaPanelSpec;
}

export interface FoundPanelSpecs {
  [key: string]: FoundNonVegaPanelSpec | FoundVegaPanelSpec | undefined;
}

export interface AddPanel {
  type: 'add';
  spec:
    | DefaultNonVegaPanelSpec
    | DefaultVegaPanelSpec
    | APIAddedPanelSpec
    | RegexPanelSpec;
}

interface UpdateNonVegaPanelSpec {
  type: 'default-panel';
  metrics: string[];
  defaultXAxis?: string;
}

interface UpdateVegaPanelSpec {
  type: 'legacy-vega';
  config: VegaPanelConfig;
}

export interface UpdatePanel {
  type: 'update';
  panelId: string;
  spec: UpdateNonVegaPanelSpec | UpdateVegaPanelSpec | APIAddedPanelSpec;
}

export type PanelBankDiff = Array<{
  key: string;
  operation: AddPanel | UpdatePanel;
}>;

export interface APIAddedPanelBankDiff {
  [key: string]: {
    type: 'update';
    panelId: string;
    spec: APIAddedPanelSpec;
  };
}

export interface FoundAPIAddedPanelSpecs {
  [key: string]: {
    config: PanelConfig;
    panelId: string;
    viewType: string;
    type: 'api-added-panel';
  };
}

export const panelViews = {
  barChart: 'Bar Chart',
  codeComparer: 'Code Comparer',
  marimo: 'Marimo',
  markdown: 'Markdown Panel',
  mediaBrowser: 'Media Browser',
  multiRunTable: 'Multi Run Table',
  parallelCoordinatesPlot: 'Parallel Coordinates Plot',
  parameterImportance: 'Parameter Importance',
  runComparer: 'Run Comparer',
  runHistoryLinePlot: 'Run History Line Plot',
  scalarChart: 'Scalar Chart',
  scatterPlot: 'Scatter Plot',
  vega: 'Vega',
  vega2: 'Vega2',
  vega3: 'Vega3',
  weave: 'Weave',
  weaveTrace: 'Weave Trace',
} as const;
export type PanelViews = (typeof panelViews)[keyof typeof panelViews];

export interface APIAddedPanelSpec {
  config: PanelConfig;
  viewType: PanelViews;
  type: 'api-added-panel';
  keyType?: RunHistoryKeyType;
  defaultSection: string;
}

export interface APIAddedPanelSpecs {
  [key: string]: APIAddedPanelSpec;
}

interface KeyQueryArgSpec {
  name: 'keys' | 'extraKeys' | 'foldKeys' | 'tableColumns';
  value: string[];
}

interface TableQueryArgSpec {
  name: 'tableKey';
  value: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface QueryArgSpecs
  extends Array<TableQueryArgSpec | KeyQueryArgSpec> {}

export interface BaseSearchHistoryItem {
  query: string;
}

export interface RunSearchHistoryItem extends BaseSearchHistoryItem {
  isRegex: boolean;
}

export interface OptionTypeBase {
  value: string;
  label: string;
}

export interface OptionTypeWithRegex extends OptionTypeBase {
  isRegex: boolean;
}

/**
 * PanelBankSettings are settings that apply to a _panel bank_.
 * For example, how sections should be organized, or how panels should
 * be sorted within each section. Settings that apply to a _panel_
 * should be defined as {@link PanelSettings.Settings} instead.
 */
export interface PanelBankSettings {
  /** whether sections should be auto-expanded during panel search */
  autoExpandSearchResults?: boolean;
  autoOrganizePrefix?: OrganizationPrefix;
  /** @deprecated use {@link PanelSettings.Settings.colorRunNames} instead */
  colorRunNames?: boolean;
  defaultMoveToSectionName?: string; // the name of the default section for the "Move panel to..." feature
  highlightedCompanionRunOnly?: boolean; // if true, only show the highlighted run in companion plots
  /** @deprecated use {@link PanelSettings.Settings.maxRuns} instead */
  maxRuns?: number;
  /** @deprecated use {@link PanelSettings.Settings.pointVisualizationMethod} instead  */
  pointVisualizationMethod?: PointVisualizationOptions;
  showEmptySections: boolean; // if false, we'll hide sections with zero panels
  sortAlphabetically: boolean; // sort panels alphabetically
  searchQuery?: string; // persists the search query in the workspace
  searchHistory?: BaseSearchHistoryItem[]; // list of past queries in this workspace
  /** tracks open/close states for sections during panel search */
  searchSectionsOpen?: {
    /** determines baseline open/close values */
    mode:
      | 'default' // use the same open states as when no search is active
      | 'auto-expand' // first MAX_AUTO_OPEN_SECTIONS sections are expanded
      | 'expand-all' // all sections are expanded
      | 'collapse-all'; // all sections are collapsed;
    /** ids of sections that were toggled to be opposite of the baseline value */
    overrides?: Array<PanelBankSectionConfig['__id__']>;
  };
  /** @deprecated use {@link PanelSettings.Settings.suppressLegends} instead */
  suppressLegends?: boolean;
  /** @deprecated use {@link PanelSettings.Settings.tooltipNumberOfRuns} instead */
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions;
}

/**
 * Sparse override for a single panel's placement.
 * Used to persist custom ordering without persisting all panels.
 */
export interface PanelPlacementOverride {
  /** Target section's __id__ (stable across renames) */
  sectionId: string;
  /** Lexicographic order key for sorting within the section */
  orderKey: string;
}

/**
 * Sparse override for a single panel's configuration.
 * Used to persist customized panel configs without persisting all panels.
 */
export interface PanelConfigOverride {
  /** Full panel config (default configs are already sparse) */
  config?: PanelConfig;
  /** Layout overrides (panel size) */
  layout?: Partial<LayoutParameters>;
}

export interface PanelBankConfig {
  state: PanelBankConfigState;
  sections: ReadonlyArray<PanelBankSectionConfig>;
  settings: PanelBankSettings; // todo(joyce): soon to be deprecated
  /**
   * Sparse overrides for panel placement.
   * Key: panelKey (from getKey(panel) for auto panels)
   * Value: { sectionId, orderKey } for custom placement
   *
   * This allows persisting custom panel ordering without persisting all panels,
   * keeping sections auto-compressible even after user reorders.
   *
   * TODO: Orphaned overrides (for metrics that no longer exist) are not yet
   * cleaned up. Add telemetry to measure accumulation before implementing GC,
   * since metrics can temporarily disappear due to run set filters.
   */
  panelPlacementOverrides?: Record<string, PanelPlacementOverride>;
  /**
   * Sparse overrides for panel configuration.
   * Key: panelKey (from getKey(panel) for auto panels)
   * Value: { config, layout } for custom configuration
   *
   * This allows persisting customized panel configs without persisting all panels,
   * keeping sections auto-compressible even after user edits.
   *
   * TODO: Orphaned overrides (for metrics that no longer exist) are not yet
   * cleaned up. Add telemetry to measure accumulation before implementing GC,
   * since metrics can temporarily disappear due to run set filters.
   */
  panelConfigOverrides?: Record<string, PanelConfigOverride>;
}

export type PanelBankConfigWithRefs = Omit<PanelBankConfig, 'sections'> & {
  sections: SectionWithRef[];
};

export interface PanelBankSectionConfig {
  /** permanent, unique ID for the section (unlike ref ID which is ephemeral) */
  __id__: string;
  /**
   * The original auto-generated section name, preserved even after the user
   * renames the section. Used during decompression to match panels to sections
   * when the display name has been changed (e.g., "Charts" → "My Charts").
   * Only set on auto-generated sections; undefined for user-created sections.
   */
  defaultName?: string;
  flowConfig?: Partial<PanelBankFlowSectionConfig>;
  /**
   * True if all panels in the section are default auto-generated panels.
   * Becomes false if any panels inside are edited, added, deleted, or moved.
   */
  isPanelsAuto?: boolean;
  isOpen: boolean;
  /** @deprecated -- these get migrated to sectionSettings on load */
  localPanelSettings?: Settings;
  name: string;
  panels: ReadonlyArray<LayedOutPanel>;
  sorted: SectionPanelSorting;
  pinned: boolean;
  /** @deprecated -- these are not longer used since we only support grid in reports and flow in workspaces now */
  type?: 'grid' | 'flow';
  sectionSettings?: OrganizedSettings; // contains section level settings
}

export interface PanelBankFlowSectionConfig {
  snapToColumns: boolean;
  columnsPerPage: number;
  rowsPerPage: number;
  gutterWidth: number;
  boxWidth: number;
  boxHeight: number;
  mobileColumnsPerPage: number;
  mobileRowsPerPage: number;
}

export interface PanelIsActiveParams {
  section: PanelBankSectionConfig;
  panel: LayedOutPanel;
  historyKeyInfo: RunHistoryKeyInfo;
  keyTypes?: {[key: string]: RunHistoryKeyType};
  isSingleRun?: boolean;
  // Note that searchRegex is optional.
  // If it is undefined, panelIsActive will return true for search hits *and* search misses
  searchRegex?: RegExp | null;
}

export interface PanelBankSectionConfigWithVisiblePanels
  extends PanelBankSectionConfig {
  ref: PanelBankSectionRef;
  visiblePanels: ReadonlyArray<LayedOutPanel>;
}

export interface PanelBankProps {
  disableRunLinks?: boolean;
  entityName: string;
  projectName: string;
  /**
   * user with readonly access to the project can still do pretty
   * much everything in the panel bank (e.g. add/edit sections,
   * panels, and settings). changes will just be exploratory/discarded
   * at the end of the session. however, actions that would actually
   * create/modify project-level resources like reports must be hidden.
   */
  isProjectReadOnly?: boolean;
  isMobileWorkspaceTab?: boolean;
  workspaceID?: string;
  isRunSelectorMinimized?: boolean;
  toggleMinimizeRunSelectorWithTracking?: (method: 'hotkey' | 'button') => void;
}

// new setting types ----

/**
 * These are the settings that are not panel specific and will be
 * used in new settings structure.
 */
export type GeneralWorkspaceSettings = {
  /** whether sections should be auto-expanded during panel search */
  autoExpandSearchResults?: boolean;

  defaultMoveToSectionName?: string; // the name of the default section for the "Move panel to..." feature

  searchQuery?: string; // persists the search query in the workspace
  searchHistory?: BaseSearchHistoryItem[]; // list of past queries in this workspace
  /** tracks open/close states for sections during panel search */
  searchSectionsOpen?: {
    /** determines baseline open/close values */
    mode:
      | 'default' // use the same open states as when no search is active
      | 'auto-expand' // first MAX_AUTO_OPEN_SECTIONS sections are expanded
      | 'expand-all' // all sections are expanded
      | 'collapse-all'; // all sections are collapsed;
    /** ids of sections that were toggled to be opposite of the baseline value */
    overrides?: Array<PanelBankSectionConfig['__id__']>;
  };
};

/**
 * This constructs an easily scannable settings structure grouped by
 * panel type.
 */
export type OrganizedSettings = GeneralWorkspaceSettings &
  WorkspaceLayoutSettings & {
    linePlot?: LinePlotSettings; // all runs line plot specific settings should go into this object
    semanticLegendRunColorOptions?: RunColorOptions; // used to determine if a semantic legend is enabled
    semanticLegendPalette?: SemanticLegendPalette; // used to determine the palette for the semantic legend
    colorPaletteOption?: ColorPaletteOption;
    media?: MediaBrowserPanelConfig;
  };

export type SectionWithRef = Omit<PanelBankSectionConfig, 'panels'> & {
  ref: PanelBankSectionRef;
  panels: LayedOutPanelWithRef[];
};

export type SectionMetadata = {
  // These are the panels that should be rendered. Note they may be a subset
  // of the panels present in config, due to data availability.
  // It's important to use the config's panels in update logic.
  activePanelRefs: ReadonlyArray<PanelTypes.Ref>;
  // These are panels that will not be rendered.  They were filtered out
  // by hasDataForPanel
  inactivePanelRefs: ReadonlyArray<PanelTypes.Ref>;
  // Map of panels that match the current searchRegex
  panelSearchResults?: Map<PanelTypes.Ref, boolean>;
  // Number of search hits in the section
  panelSearchMatchCount?: number;
  // Does the section name match the search query?
  isSectionNameSearchMatch?: boolean;
};

export type SectionWithMetadata = SectionWithRef & SectionMetadata;

export type PanelDropRef = {
  insertDir: 'before' | 'after';
  panelRef: PanelTypes.Ref | undefined;
  sectionRef: PanelBankSectionRef;
};
